import { ProgressCallback } from "../types/callbacks";
import OpenAI from "openai";
import { getEnvironmentVariable } from "./supabase";

// Queue to manage API requests
let requestQueue: Promise<any> = Promise.resolve();

export interface GeneratePatternResponse {
  pattern: string;
  message?: string;
  error?: string;
}

function getContextualMessage(prompt: string, stage: string): string {
  // Extract key terms from the prompt
  const promptLower = prompt.toLowerCase();
  const isHero = promptLower.includes("hero");
  const isButton = promptLower.includes("button");
  const isLayout =
    promptLower.includes("layout") ||
    promptLower.includes("column") ||
    promptLower.includes("grid");
  const isStyle =
    promptLower.includes("style") ||
    promptLower.includes("color") ||
    promptLower.includes("font");

  switch (stage) {
    case "init":
      if (isHero) return "Preparing to build your new hero section...";
      if (isButton) return "Getting ready to create your button component...";
      if (isLayout) return "Planning your new layout structure...";
      if (isStyle) return "Preparing to apply your style changes...";
      return "Analyzing your request...";

    case "create":
      if (isHero) return "Crafting the perfect hero section...";
      if (isButton) return "Designing your button component...";
      if (isLayout) return "Structuring your new layout...";
      if (isStyle) return "Applying your style preferences...";
      return "Creating your pattern...";

    case "process":
      if (isHero) return "Optimizing hero section components...";
      if (isButton) return "Fine-tuning button interactions...";
      if (isLayout) return "Arranging layout elements...";
      if (isStyle) return "Perfecting the visual details...";
      return "Processing your pattern...";

    case "finalize":
      if (isHero) return "Finalizing your hero section...";
      if (isButton) return "Completing button implementation...";
      if (isLayout) return "Completing layout arrangement...";
      if (isStyle) return "Finalizing style changes...";
      return "Finalizing your pattern...";

    case "complete":
      if (isHero) return "Your hero section is ready!";
      if (isButton) return "Your button component is ready!";
      if (isLayout) return "Your new layout is ready!";
      if (isStyle) return "Your style changes are complete!";
      return "Your pattern is ready!";

    default:
      return "Processing your request...";
  }
}

// Helper function to get the OpenAI API key from various sources
async function getOpenAIApiKey(): Promise<string | null> {
  // Try to get from environment variables first
  const envApiKey =
    process.env.VITE_OPENAI_API_KEY || import.meta.env.VITE_OPENAI_API_KEY;
  if (envApiKey) {
    return envApiKey;
  }

  // If not in environment, try to get from Supabase as fallback
  try {
    const supabaseApiKey = await getEnvironmentVariable("VITE_OPENAI_API_KEY");
    if (supabaseApiKey) {
      return supabaseApiKey;
    }
  } catch (error) {
    console.error("Error retrieving OpenAI API key:", error);
  }

  return null;
}

export async function generatePattern(
  prompt: string,
  existingPattern?: string,
  onProgress?: ProgressCallback
): Promise<GeneratePatternResponse> {
  const currentRequest = requestQueue.then(async () => {
    try {
      onProgress?.(10, getContextualMessage(prompt, "init"));

      // Get API key
      const apiKey = await getOpenAIApiKey();

      if (!apiKey) {
        throw new Error(
          "OpenAI API key not found. Please add your API key to the Netlify environment variables with the name 'VITE_OPENAI_API_KEY'."
        );
      }

      // Initialize OpenAI client
      const openai = new OpenAI({
        apiKey,
        dangerouslyAllowBrowser: true, // Required for client-side usage
      });

      onProgress?.(25, getContextualMessage(prompt, "create"));

      // Create a thread
      const thread = await openai.beta.threads.create();

      // Add the message to the thread
      await openai.beta.threads.messages.create(thread.id, {
        role: "user",
        content: existingPattern
          ? `Modify this WordPress block pattern according to these instructions: "${prompt}"\n\nCurrent pattern:\n${existingPattern}`
          : prompt,
      });

      onProgress?.(50, getContextualMessage(prompt, "process"));
      // Run the assistant
      const run = await openai.beta.threads.runs.create(thread.id, {
        assistant_id: "asst_U6CgYlZFN0DZUIX25nO5N1sN",
      });

      // Poll for completion
      let completedRun;
      while (true) {
        const runStatus = await openai.beta.threads.runs.retrieve(
          thread.id,
          run.id
        );
        if (runStatus.status === "completed") {
          completedRun = runStatus;
          break;
        } else if (runStatus.status === "failed") {
          throw new Error(
            "Assistant run failed: " + runStatus.last_error?.message
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second before polling again
      }

      onProgress?.(75, getContextualMessage(prompt, "finalize"));
      // Get the messages
      const messages = await openai.beta.threads.messages.list(thread.id);
      const lastMessage = messages.data[0]; // Get the most recent message

      if (
        !lastMessage ||
        !lastMessage.content[0] ||
        lastMessage.content[0].type !== "text"
      ) {
        throw new Error("No valid response from assistant");
      }

      const pattern = lastMessage.content[0].text.value.trim();

      onProgress?.(100, getContextualMessage(prompt, "complete"));

      return {
        pattern,
        error: undefined,
      };
    } catch (error) {
      console.error("Error generating pattern:", error);
      return {
        pattern: "",
        error:
          error instanceof Error
            ? error.message || "Failed to generate pattern"
            : "An unexpected error occurred while generating the pattern",
      };
    }
  });

  requestQueue = currentRequest.catch(() => {});
  return currentRequest;
}
